import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { listJizdyWeek } from '@/api/services/jizdyService';
import type { CalendarFilters, JizdaListItem } from '@/api/types/Jizda';
import { getWeekStart, todayIsoDate } from '@/utils/date';

import type { RootState } from './index';

// Weekly transport calendar state (CHR-03 §6 / ADR-0051). Mirrors
// `ordersSlice` conventions: a `loadWeek` thunk reads the slice's `weekStart`
// + `filters` so the toolbar controls and the fetch stay in sync. The calendar
// is read-only and fetched in one bounded call (no cursor pagination), so
// there is no `hasMore`/`cursor` plumbing — just `items`/`status`/`error`.

export type CalendarStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface CalendarState {
  // Monday (ISO LocalDate) of the displayed week. Normalised on every set.
  weekStart: string;
  filters: CalendarFilters;
  items: JizdaListItem[];
  status: CalendarStatus;
  error: string | null;
  // STALE-RESPONSE GUARD (BUG-1, ported from the chr-04 e2e-suite-repair —
  // same fix as `ordersSlice`). Week arrows and `?week=`
  // navigation fire `loadWeek` in quick succession and the calendar holds ONE
  // week at a time, so a slow FIRST week can land after a fast SECOND one and
  // repaint the previous week's fůry under the new week's date headers.
  latestRequestId: string | null;
}

const initialState: CalendarState = {
  weekStart: getWeekStart(todayIsoDate()),
  filters: {
    showDeleted: false,
    showStorno: true,
  },
  items: [],
  status: 'idle',
  error: null,
  latestRequestId: null,
};

// Loads the displayed week's jízdy in one bounded fetch. Consumes
// `state.calendar.weekStart` + `state.calendar.filters` so callers only mutate
// state and re-dispatch (same pattern as `loadOrdersPage`).
export const loadWeek = createAsyncThunk<
  JizdaListItem[],
  void,
  { state: RootState; rejectValue: string }
>('calendar/loadWeek', async (_arg, { getState, rejectWithValue }) => {
  const { weekStart, filters } = getState().calendar;
  try {
    return await listJizdyWeek(weekStart, filters);
  } catch (e) {
    const message = e instanceof Error ? e.message : 'unknown';
    return rejectWithValue(message);
  }
});

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    // Sets the displayed week, normalising any ISO date to its Monday so the
    // bounded fetch window is stable regardless of which weekday the caller
    // passes (e.g. a `?week=` query param a user hand-edited mid-week).
    setWeekStart(state, action: PayloadAction<string>) {
      state.weekStart = getWeekStart(action.payload);
    },
    setCalendarFilters(state, action: PayloadAction<Partial<CalendarFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadWeek.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(loadWeek.fulfilled, (state, action) => {
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = 'ready';
        state.error = null;
        state.items = action.payload;
      })
      .addCase(loadWeek.rejected, (state, action) => {
        // A superseded week that fails must not blank the week the user is
        // actually looking at — the guard covers BOTH terminal cases.
        if (state.latestRequestId !== action.meta.requestId) return;
        state.status = 'error';
        state.error = action.payload ?? 'unknown';
      });
  },
});

export const { setWeekStart, setCalendarFilters } = calendarSlice.actions;
export const calendarReducer = calendarSlice.reducer;

// Selectors
export const selectCalendarWeekStart = (state: RootState): string =>
  state.calendar.weekStart;
export const selectCalendarFilters = (state: RootState): CalendarFilters =>
  state.calendar.filters;
export const selectCalendarItems = (state: RootState): JizdaListItem[] =>
  state.calendar.items;
export const selectCalendarStatus = (state: RootState): CalendarStatus =>
  state.calendar.status;
export const selectCalendarError = (state: RootState): string | null =>
  state.calendar.error;
